import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to send Bearer token on every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('pg_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh interceptor: on 401, try refreshing the token once
let isRefreshing = false;
let refreshQueue = [];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Org lifecycle (423 Locked) — org is suspended or terminated.
    // Stash the detail in sessionStorage and redirect to the friendly status page.
    if (error.response?.status === 423) {
      const d = error.response.data?.detail || {};
      if (d.code === 'org_suspended' || d.code === 'org_terminated') {
        try {
          sessionStorage.setItem('pg_org_status_info', JSON.stringify(d));
        } catch (_e) { /* ignore quota */ }
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/account-status')) {
          window.location.replace('/account-status');
        }
        return Promise.reject(error);
      }
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register') &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/me')
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      isRefreshing = true;
      const refreshToken = localStorage.getItem('pg_refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API}/auth/refresh`, {
          refresh_token: refreshToken
        });
        if (data.token) {
          localStorage.setItem('pg_token', data.token);
        }
        refreshQueue.forEach(({ resolve }) => resolve());
        refreshQueue = [];
        return axios(originalRequest);
      } catch (refreshError) {
        refreshQueue.forEach(({ reject }) => reject(refreshError));
        refreshQueue = [];
        localStorage.removeItem('pg_token');
        localStorage.removeItem('pg_refresh_token');
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('pg_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      localStorage.removeItem('pg_token');
      localStorage.removeItem('pg_refresh_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.token) {
        localStorage.setItem('pg_token', data.token);
      }
      if (data.refresh_token) {
        localStorage.setItem('pg_refresh_token', data.refresh_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message 
      };
    }
  };

  const register = async (email, password, name, termsAccepted = false, inviteToken = null, companyName = null) => {
    try {
      let url = `${API}/auth/register`;
      if (inviteToken) url += `?invite=${inviteToken}`;
      const body = { email, password, name, terms_accepted: termsAccepted };
      if (companyName) body.company_name = companyName;
      const { data } = await axios.post(url, body);
      if (data.token) {
        localStorage.setItem('pg_token', data.token);
      }
      if (data.refresh_token) {
        localStorage.setItem('pg_refresh_token', data.refresh_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      localStorage.removeItem('pg_token');
      localStorage.removeItem('pg_refresh_token');
      setUser(false);
    }
  };

  const acceptTerms = async () => {
    try {
      await axios.post(`${API}/auth/accept-terms`, {});
      setUser(prev => prev ? { ...prev, terms_accepted: true } : prev);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  // Re-fetch the user (used after email verification to flip email_verified=true)
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, []);

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    logout,
    acceptTerms,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
